import type { AutocompleteProps } from "@mui/material"
import { Autocomplete, Checkbox, Divider, ListItemText, MenuItem, MenuList, TextField } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
  type ComponentType,
  type HTMLAttributes,
  type ReactElement,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react"

import { type AerosTagData, AerosTagStack } from "@/components/UiKit"
import { DIVIDER_HEIGHT, MENU_ITEM_HEIGHT, MENU_MAX_HEIGHT } from "@/constants/forms"
import type { Option } from "@/types/form-fields"

interface ListboxProps extends React.HTMLAttributes<HTMLElement> {
  withSelectAll: boolean
  enabledOptionsLength: number
  isAllSelected: boolean
  isIndeterminate: boolean
  onSelectAll: () => void
}

const Listbox = forwardRef<HTMLUListElement, ListboxProps>(
  (
    { withSelectAll, enabledOptionsLength, isAllSelected, isIndeterminate, onSelectAll, children, ...menuListProps },
    ref
  ) => {
    const parentRef = useRef<HTMLDivElement>(null)

    const childrenArray = useMemo(() => (children as React.ReactNode[]) ?? [], [children])

    const SELECT_ALL_HEIGHT = withSelectAll && enabledOptionsLength > 0 ? MENU_ITEM_HEIGHT + DIVIDER_HEIGHT : 0

    const rowVirtualizer = useVirtualizer({
      count: childrenArray.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => MENU_ITEM_HEIGHT,
      overscan: 5, // Render 5 extra items above/below viewport for smooth scrolling
    })

    const virtualItems = rowVirtualizer.getVirtualItems()
    const totalHeight = rowVirtualizer.getTotalSize()

    return (
      <MenuList
        {...menuListProps}
        ref={ref}
        style={{
          maxHeight: MENU_MAX_HEIGHT,
          ...menuListProps.style,
        }}
      >
        {/* Fixed Header: Select All (not virtualized) */}
        {withSelectAll && enabledOptionsLength > 0 && (
          <>
            <MenuItem key='select-all' onClick={onSelectAll}>
              <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
              <ListItemText primary='Select All' />
            </MenuItem>
            <Divider key='divider' className='!my-0' />
          </>
        )}
        <div
          ref={parentRef}
          style={{
            height: Math.min(totalHeight, MENU_MAX_HEIGHT - SELECT_ALL_HEIGHT),
            overflow: "auto",
            position: "relative",
          }}
        >
          <div
            style={{
              height: `${totalHeight}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualItems.map((virtualRow) => {
              const child = childrenArray[virtualRow.index]
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {child}
                </div>
              )
            })}
          </div>
        </div>
      </MenuList>
    )
  }
)

export type AerosMultiAutocompleteProps<T extends string | number = string> = Omit<
  AutocompleteProps<Option<T>, true, false, false>,
  "onChange" | "renderInput" | "options" | "value" | "multiple" | "renderTags" | "freeSolo"
> & {
  options: Option<T>[]
  value?: T[]
  label?: string
  placeholder?: string
  helperText?: string
  required?: boolean
  error?: boolean
  withSelectAll?: boolean
  freeSolo?: boolean
  onChange?: (value: T[]) => void
  "data-testid"?: string
}

export const AerosMultiAutocomplete = forwardRef(
  <T extends string | number = string>(
    {
      options,
      value,
      label,
      placeholder = "Search and select options",
      helperText,
      required,
      error,
      withSelectAll = false,
      freeSolo = false,
      onChange,
      "data-testid": testId,
      ...props
    }: AerosMultiAutocompleteProps<T>,
    ref: React.Ref<HTMLDivElement>
  ): ReactElement => {
    const selectedValues = useMemo(() => value || [], [value]) as T[]
    const enabledOptions = options.filter((opt) => !opt.disabled)
    const enabledValues = enabledOptions.map((opt) => opt.value)

    const isAllSelected = selectedValues.length === enabledValues.length && enabledValues.length > 0
    const isIndeterminate = selectedValues.length > 0 && selectedValues.length < enabledValues.length

    const matchedOptions = useMemo(() => {
      if (freeSolo) {
        return selectedValues.map((val) => {
          const option = options.find((o) => o.value === val)
          return option ?? String(val)
        })
      }
      return selectedValues
        .map((val) => options.find((option) => option.value === val))
        .filter((option): option is Option<T> => option !== undefined)
    }, [selectedValues, options, freeSolo])

    const handleChange = (_event: unknown, selectedOptions: (Option<T> | string)[]) => {
      const values = selectedOptions.map((option) =>
        typeof option === "string" ? (option as unknown as T) : option.value
      )
      onChange?.(values)
    }

    const handleSelectAll = useCallback(() => {
      onChange?.(isAllSelected ? [] : enabledValues)
    }, [isAllSelected, enabledValues, onChange])

    const handleTagDelete = (tagToDelete: AerosTagData) => {
      const values = selectedValues.filter((value: T) => value !== tagToDelete.value)
      onChange?.(values)
    }

    const tags: AerosTagData[] = selectedValues.map((value) => {
      const selectedOption = options.find((option) => option.value === value)
      return {
        key: value,
        label: selectedOption?.label ?? String(value),
        value,
      }
    })

    const listboxSlotProps: ListboxProps = useMemo(
      () => ({
        withSelectAll: withSelectAll && !freeSolo,
        enabledOptionsLength: enabledOptions.length,
        isAllSelected,
        isIndeterminate,
        onSelectAll: handleSelectAll,
      }),
      [withSelectAll, freeSolo, enabledOptions.length, isAllSelected, isIndeterminate, handleSelectAll]
    )

    return (
      <Autocomplete<Option<T>, true, false, false>
        {...props}
        ref={ref}
        value={matchedOptions}
        options={options}
        fullWidth
        multiple
        freeSolo={freeSolo}
        autoSelect={freeSolo}
        disableCloseOnSelect={!freeSolo}
        onChange={handleChange}
        getOptionLabel={(option: Option<T> | string) => {
          if (typeof option === "string") return option
          return option.label
        }}
        isOptionEqualToValue={(option: Option<T> | string, selectedOption: Option<T> | string) => {
          if (typeof option === "string" || typeof selectedOption === "string") return option === selectedOption
          return option.value === selectedOption.value
        }}
        getOptionDisabled={(option: Option<T> | string) => {
          if (typeof option === "string") return false
          return option.disabled ?? false
        }}
        popupIcon={<KeyboardArrowDownOutlined />}
        slots={{
          listbox: Listbox as unknown as ComponentType<HTMLAttributes<HTMLElement>>,
        }}
        slotProps={{
          listbox: listboxSlotProps,
        }}
        renderTags={() => (
          <AerosTagStack className='flex-1' tags={tags} onDelete={handleTagDelete} disabled={props.disabled} />
        )}
        renderOption={(optionProps: object, option: Option<T> | string, { selected }: { selected: boolean }) => {
          if (typeof option === "string") {
            return (
              <MenuItem {...optionProps} key={option}>
                <ListItemText primary={option} />
              </MenuItem>
            )
          }
          return (
            <MenuItem
              {...optionProps}
              key={option.value}
              disabled={option.disabled}
              className={withSelectAll && !freeSolo ? "pl-8" : undefined}
            >
              {!freeSolo && <Checkbox key={`checkbox-${option.value}`} checked={selected} />}
              <ListItemText key={`list-item-text-${option.value}`} primary={option.label} />
            </MenuItem>
          )
        }}
        renderInput={(params: object) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder={selectedValues.length === 0 ? placeholder : undefined}
            error={error}
            helperText={helperText}
            variant='outlined'
            data-testid={testId}
          />
        )}
      />
    )
  }
) as <T extends string | number = string>(
  props: AerosMultiAutocompleteProps<T> & { ref?: React.Ref<unknown> }
) => ReactElement
